import ADDRESS_METADATA from '@/constants/address-metadata.json';



/*
  Decide which country a form's country selector should pre-select.

  The signal we lean on first is `navigator.language` — the BCP 47 tag the
  browser exposes for the user's locale. Its region subtag (e.g. "GB" in
  "en-GB") is treated as the user's country. We deliberately do NOT route
  this through `constants/supported-locales.js` (which is locale-scoped for
  time/currency formatting) — country detection works for any of the 250+
  countries chromium publishes address metadata for.

  Precedence:
    1. Device country, if it's in the allowlist (or no allowlist was given).
    2. First entry in the allowlist.
    3. 'US' as a last-resort fallback.
*/

const FALLBACK_COUNTRY = 'US';



/**
 * Parse the ISO 3166-1 alpha-2 country code from `navigator.language`.
 * Returns null when the tag has no region subtag, the region isn't an
 * ISO-shaped 2-letter code, or chromium doesn't publish data for it.
 *
 * @returns {string|null}
 */
function detectDeviceCountry() {
  const tag = (typeof window !== 'undefined' && window.navigator && window.navigator.language) || '';
  const region = tag.split('-')[1];

  if (!region || region.length !== 2) return null;

  const upper = region.toUpperCase();
  return ADDRESS_METADATA[upper] ? upper : null;
}



/**
 * Pick the default country for a form's country selector.
 *
 * @param {string[]} [allowedCountries] - ISO codes the selector exposes.
 *   Omit or pass an empty array to allow every country (credit-card-billing
 *   case).
 * @returns {string}
 */
function detectDefaultCountry(allowedCountries) {
  const deviceCountry = detectDeviceCountry();
  const hasAllowlist = Array.isArray(allowedCountries) && allowedCountries.length > 0;

  if (hasAllowlist) {
    if (deviceCountry && allowedCountries.includes(deviceCountry)) {
      return deviceCountry;
    }
    return allowedCountries[0];
  }

  return deviceCountry || FALLBACK_COUNTRY;
}



export { detectDeviceCountry, detectDefaultCountry };
